import React, { PropsWithChildren, useReducer, useCallback, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { action } from 'typesafe-actions';
import { useLocalStorage } from 'react-use';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { useContext } from 'use-context-selector';
import { useAppAuthenticationTokenParsed } from '@dxb-web-ui-framework/iam/contexts/AppAuthentication/hooks';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { AppAuthenticationContext } from '@dxb-web-ui-framework/iam/contexts/AppAuthentication';
import { dashboardService } from '../../../../../channels/secure-web/services';
import { DashboardData } from '../../domain/UserInfo/entities';
import { justLoggedIn } from '../../utils';
import { UserInfoContext } from './UserInfoContext';
import { defaultUserInfoProviderState } from './defaultState';
import { UserInfoProviderProviderProps, UserProfileActionTypes } from './types';
import { UserProfileReducer } from './UserProfileReducer';

const UserInfoProvider = ({
  entitlements,
  loaderComponent,
  errorComponent,
  children,
}: PropsWithChildren<UserInfoProviderProviderProps>) => {
  const [state, dispatch] = useReducer(UserProfileReducer, defaultUserInfoProviderState(entitlements));
  const tokenParsed = useAppAuthenticationTokenParsed();
  const [defaultCustomerList] = useLocalStorage<GenericObject | null>('customer-list');
  const { logout } = useContext(AppAuthenticationContext);
  // Initiate API requests
  const {
    data: dashboardData,
    error: dashboardError,
    isLoading: dashboardIsLoading,
    mutate: refetchDashboardData,
  } = useInvokeSWRService(
    dashboardService.GetDashboardData as () => Promise<DashboardData>,
    undefined,
    true,
    'dashboardService.GetDashboardData',
  );

  const currentUrl = window.location.pathname?.split('/secure-web/')?.[1]?.split('/') ?? [];
  const [customerIdUrl, ...restUrl] = currentUrl;
  const restPath = restUrl.length > 0 ? `/${restUrl.join('/')}` : undefined;
  const shouldFetchEntitlementsData = Boolean(state.selectedCustomerId);

  const {
    data: entitlementsData,
    error: entitlementsError,
    isLoading: isLoadingEntitlementsData,
  } = useInvokeSWRService(
    dashboardService.GetEntitlementsData,
    { companyId: state.selectedCustomerId },
    shouldFetchEntitlementsData,
    'dashboardService.GetEntitlementsData',
  );

  const onSelectCustomerId = useCallback((id: string) => {
    dispatch(action(UserProfileActionTypes.setSelectedCustomerId, id));
  }, []);

  const isJustLoggedIn = justLoggedIn({ start: new Date(tokenParsed?.auth_time * 1000), end: new Date() });

  const isValidCustomerId = useCallback(
    customerId => !!customerId && !!dashboardData?.customers?.find(({ customerId: cId }) => cId === customerId),
    [dashboardData?.customers],
  );

  const getCustomerId = useCallback(
    () =>
      isValidCustomerId(customerIdUrl)
        ? customerIdUrl
        : dashboardData &&
          defaultCustomerList &&
          isValidCustomerId(defaultCustomerList[dashboardData.userInfo?.omniUserId])
        ? defaultCustomerList[dashboardData.userInfo?.omniUserId]
        : dashboardData?.customers?.[0]?.customerId,
    [defaultCustomerList, isValidCustomerId, customerIdUrl, dashboardData],
  );

  useEffect(() => {
    if (entitlementsData?.prospect) {
      if (entitlementsData?.onboardingRedirectURL) {
        localStorage.removeItem('customer-list');
        logout({ redirectUri: entitlementsData?.onboardingRedirectURL });
      }
    }
  }, [entitlementsData, logout]);

  if (entitlementsData?.prospect) {
    if (!entitlementsData?.onboardingRedirectURL) {
      return <>{errorComponent}</>;
    }
    return <>{loaderComponent}</>;
  }

  return (
    <UserInfoContext.Provider
      value={{
        ...state,
        entitlements:
          shouldFetchEntitlementsData && entitlementsData?.appTransactions
            ? entitlementsData?.appTransactions
            : entitlements,
        userInfo: dashboardData?.userInfo ?? null,
        customerList: dashboardData?.customers ?? null,
        onSelectCustomerId,
        refetchDashboardData,
        loadingState: {
          isLoadingEntitlementsData,
        },
      }}>
      {dashboardData && !state.selectedCustomerId && (
        <Navigate to={`/${getCustomerId()}/${isJustLoggedIn ? 'dashboard' : restPath ?? 'dashboard'}`} />
      )}
      {(dashboardError || entitlementsError) && errorComponent}
      {(dashboardIsLoading || isLoadingEntitlementsData) && loaderComponent}
      {dashboardData && children}
    </UserInfoContext.Provider>
  );
};

export { UserInfoProvider };
